/**
 * Token Configuration Service
 * Fetches token costs and limits from the backend database
 * This is the SINGLE SOURCE OF TRUTH for all token configuration
 */

import { axios } from "@/client/libs/axios";

// Type definitions for token config
export interface TokenConfigFlat {
  // Token Balances
  GUEST_TOKEN_BALANCE: number;
  REGISTERED_TOKEN_BALANCE: number;

  // Token Limits by Tier
  FREE_TOKEN_LIMIT: number;
  BASIC_TOKEN_LIMIT: number;
  PROFESSIONAL_TOKEN_LIMIT: number;
  ENTERPRISE_TOKEN_LIMIT: number;

  // Resume & Document Generation Costs
  RESUME_GENERATION_COST: number;
  SUMMARY_GENERATION_COST: number;
  COVER_LETTER_COST: number;

  // Export Costs
  PDF_EXPORT_COST: number;
  WORD_EXPORT_COST: number;

  // Analysis & Scoring Costs
  ADVANCED_RESUME_ANALYSIS_COST: number;
  ANALYTICS_COST: number;
  JOB_FIT_SCORE_COST: number;
  ATS_SCORE_COST: number;

  // AI Writing Tools Costs
  AI_SUMMARY_COST: number;
  IMPROVE_WRITING_COST: number;
  FIX_SPELLING_GRAMMAR_COST: number;
  CHANGE_TONE_COST: number;

  // Premium Features Costs
  BOOSTER_COST: number;
  TRAJECTORY_COST: number;
  LOCALIZATION_COST: number;
  LINKEDIN_REWRITE_COST: number;

  // AI Gateway/MCP Costs
  MARKET_SCOUT_COST: number;
  EXTRACT_PDF_RESUME_COST: number;
  GENERATE_RESUME_COST: number;
  FORMAT_RESUME_COST: number;
  INTERVIEW_QUESTION_COST: number;
  AI_URL_JOB_DETAILS_COST: number;

  // Resume Limits by Tier
  GUEST_RESUME_LIMIT: number;
  FREE_RESUME_LIMIT: number;
  BASIC_RESUME_LIMIT: number;
  PROFESSIONAL_RESUME_LIMIT: number;
  ENTERPRISE_RESUME_LIMIT: number;

  // Allow additional keys
  [key: string]: number;
}

// Default fallback values (used only if API fails)
const DEFAULT_CONFIG: TokenConfigFlat = {
  // Token Balances
  GUEST_TOKEN_BALANCE: 250,
  REGISTERED_TOKEN_BALANCE: 550,

  // Token Limits by Tier
  FREE_TOKEN_LIMIT: 250,
  BASIC_TOKEN_LIMIT: 1400, // Increased from 700 to support 3+ complete job applications
  PROFESSIONAL_TOKEN_LIMIT: 2000,
  ENTERPRISE_TOKEN_LIMIT: 100000,

  // Resume & Document Generation Costs
  // Note: RESUME_GENERATION_COST reflects the TOTAL streaming pipeline cost
  // (MARKET_SCOUT + GENERATE_RESUME + FORMAT_RESUME = 0+100+0 = 100)
  RESUME_GENERATION_COST: 100,
  COVER_LETTER_COST: 100,

  // Export Costs
  PDF_EXPORT_COST: 25,
  WORD_EXPORT_COST: 40,

  // Analysis & Scoring Costs
  ADVANCED_RESUME_ANALYSIS_COST: 150,
  ANALYTICS_COST: 100,
  JOB_FIT_SCORE_COST: 100,
  ATS_SCORE_COST: 100,

  // AI Writing Tools Costs (Unified summary cost = 30 tokens)
  AI_SUMMARY_COST: 30,
  SUMMARY_GENERATION_COST: 30, // Alias for backward compatibility (same as AI_SUMMARY)
  IMPROVE_WRITING_COST: 10,
  FIX_SPELLING_GRAMMAR_COST: 5,
  CHANGE_TONE_COST: 10,

  // Premium Features Costs
  BOOSTER_COST: 200,
  TRAJECTORY_COST: 100,
  LOCALIZATION_COST: 100,
  LINKEDIN_REWRITE_COST: 200,

  // AI Gateway/MCP Costs
  MARKET_SCOUT_COST: 100,
  EXTRACT_PDF_RESUME_COST: 100,
  GENERATE_RESUME_COST: 100,
  FORMAT_RESUME_COST: 50,
  INTERVIEW_QUESTION_COST: 50,
  AI_URL_JOB_DETAILS_COST: 30,

  // Resume Limits by Tier
  GUEST_RESUME_LIMIT: 1,
  FREE_RESUME_LIMIT: 2,
  BASIC_RESUME_LIMIT: 5,
  PROFESSIONAL_RESUME_LIMIT: 10,
  ENTERPRISE_RESUME_LIMIT: 999,
};

class TokenConfigService {
  private config: TokenConfigFlat | null = null;
  private configPromise: Promise<TokenConfigFlat> | null = null;
  private lastFetchTime: number = 0;
  private readonly CACHE_TTL = 10 * 60 * 1000; // 10 minutes cache - reduce API calls

  /**
   * Get the full token configuration
   * Fetches from API on first call, then caches
   */
  async getConfig(forceRefresh = false): Promise<TokenConfigFlat> {
    const now = Date.now();

    // Return cached config if valid
    if (!forceRefresh && this.config && now - this.lastFetchTime < this.CACHE_TTL) {
      return this.config;
    }

    // If a fetch is already in progress, wait for it
    if (this.configPromise) {
      return this.configPromise;
    }

    // Fetch from API
    this.configPromise = this.fetchConfig();

    try {
      this.config = await this.configPromise;
      this.lastFetchTime = now;
      return this.config;
    } finally {
      this.configPromise = null;
    }
  }

  /**
   * Fetch config from the API (public endpoint, no auth required)
   */
  private async fetchConfig(): Promise<TokenConfigFlat> {
    try {
      // Use public config endpoint (no auth required)
      const response = await axios.get<TokenConfigFlat>("/config/tokens");
      return response.data;
    } catch (error) {
      console.warn("⚠️ Failed to fetch token config from API, using defaults:", error);
      return DEFAULT_CONFIG;
    }
  }

  /**
   * Get a specific config value
   */
  async getValue(key: keyof TokenConfigFlat): Promise<number> {
    const config = await this.getConfig();
    return config[key] ?? DEFAULT_CONFIG[key] ?? 0;
  }

  /**
   * Get config value synchronously (returns cached value or default)
   * Use this for immediate access when you don't want to wait for API
   */
  getValueSync(key: keyof TokenConfigFlat): number {
    if (this.config) {
      return this.config[key] ?? DEFAULT_CONFIG[key] ?? 0;
    }
    return DEFAULT_CONFIG[key] ?? 0;
  }

  /**
   * Invalidate cache (call when admin updates config)
   */
  invalidateCache() {
    this.config = null;
    this.lastFetchTime = 0;
  }

  /**
   * Pre-load config (call during app initialization)
   */
  async preload(): Promise<void> {
    await this.getConfig();
  }

  /**
   * Get all token costs (for backward compatibility)
   */
  async getTokenCosts(): Promise<Record<string, number>> {
    const config = await this.getConfig();

    // Return only cost-related keys
    const costKeys = Object.keys(config).filter((k) => k.endsWith("_COST"));
    const costs: Record<string, number> = {};

    for (const key of costKeys) {
      costs[key] = config[key];
    }

    return costs;
  }

  /**
   * Get token limits by tier
   */
  async getTokenLimits(): Promise<Record<string, number>> {
    const config = await this.getConfig();

    return {
      FREE: config.FREE_TOKEN_LIMIT,
      BASIC: config.BASIC_TOKEN_LIMIT,
      PROFESSIONAL: config.PROFESSIONAL_TOKEN_LIMIT,
      ENTERPRISE: config.ENTERPRISE_TOKEN_LIMIT,
    };
  }

  /**
   * Get resume limits by tier
   */
  async getResumeLimits(): Promise<Record<string, number>> {
    const config = await this.getConfig();

    return {
      GUEST: config.GUEST_RESUME_LIMIT,
      FREE: config.FREE_RESUME_LIMIT,
      BASIC: config.BASIC_RESUME_LIMIT,
      PROFESSIONAL: config.PROFESSIONAL_RESUME_LIMIT,
      ENTERPRISE: config.ENTERPRISE_RESUME_LIMIT,
    };
  }
}

// Export singleton instance
export const tokenConfigService = new TokenConfigService();

// Export commonly used cost accessors for convenience
export const getResumeCost = () => tokenConfigService.getValueSync("RESUME_GENERATION_COST");
export const getSummaryCost = () => tokenConfigService.getValueSync("SUMMARY_GENERATION_COST");
export const getPdfExportCost = () => tokenConfigService.getValueSync("PDF_EXPORT_COST");
export const getWordExportCost = () => tokenConfigService.getValueSync("WORD_EXPORT_COST");
export const getGuestTokenBalance = () => tokenConfigService.getValueSync("GUEST_TOKEN_BALANCE");
export const getRegisteredTokenBalance = () =>
  tokenConfigService.getValueSync("REGISTERED_TOKEN_BALANCE");

/**
 * Get the total cost for streaming resume generation pipeline
 * This is the actual cost charged (Market Scout + Generate + Format)
 */
export const getStreamingGenerationCost = () => {
  const marketScout = tokenConfigService.getValueSync("MARKET_SCOUT_COST");
  const generate = tokenConfigService.getValueSync("GENERATE_RESUME_COST");
  const format = tokenConfigService.getValueSync("FORMAT_RESUME_COST");
  return marketScout + generate + format;
};

// Export the default config for components that need immediate values
export { DEFAULT_CONFIG };
